import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { getFirestore, collection, query, getDocs } from "firebase/firestore";
import "./Dashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const formatNumber = (num) => num.toLocaleString("en-US", { maximumFractionDigits: 0 });
const toTwoDigits = (value) => String(value).padStart(2, "0");
const chartTextColor = "#f3e9de";
const chartGridColor = "rgba(181, 168, 157, 0.2)";
const chartProjectionColor = "#9ca3af";
const buildProjectionSeries = ({ sourceSeries, fromIndex, toIndex, projectedValue }) =>
  sourceSeries.map((_, index) => {
    if (index === fromIndex) {
      return sourceSeries[fromIndex] ?? 0;
    }
    if (index === toIndex) {
      return projectedValue;
    }
    return null;
  });

const buildTargetMonths = (today = new Date()) => {
  const currentYear = today.getFullYear();
  const currentMonthNumber = today.getMonth() + 1;
  const lastMonthDate = new Date(currentYear, currentMonthNumber - 2, 1);
  const targetMonths = [];

  for (let month = 1; month <= currentMonthNumber; month += 1) {
    targetMonths.push({
      yearId: String(currentYear),
      monthId: toTwoDigits(month),
    });
  }

  if (lastMonthDate.getFullYear() !== currentYear) {
    targetMonths.push({
      yearId: String(lastMonthDate.getFullYear()),
      monthId: toTwoDigits(lastMonthDate.getMonth() + 1),
    });
  }

  return {
    currentYear,
    currentMonthNumber,
    lastMonthDate,
    targetMonths,
  };
};

const Dashboard = () => {
  const [monthlyData, setMonthlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [graphData, setGraphData] = useState({
    net: [],
    gross: [],
    totalPayments: { new: [], recurring: [] },
  });
  const [dailyGraphData, setDailyGraphData] = useState({
    totalPayments: { new: [], recurring: [] },
  });
  const [tableData, setTableData] = useState([]);
  const [yearlyTotals, setYearlyTotals] = useState({});
  const [currentMonthData, setCurrentMonthData] = useState({ net: 0, projected: 0 });
  const [recentPaymentCounts, setRecentPaymentCounts] = useState({ today: 0, yesterday: 0 });

  useEffect(() => {
    const fetchPremiumAnalytics = async () => {
      const db = getFirestore();
      const today = new Date();
      const todayKey = `${today.getFullYear()}-${toTwoDigits(today.getMonth() + 1)}-${toTwoDigits(
        today.getDate()
      )}`;
      const yesterdayDate = new Date(today);
      yesterdayDate.setDate(today.getDate() - 1);
      const yesterdayKey = `${yesterdayDate.getFullYear()}-${toTwoDigits(
        yesterdayDate.getMonth() + 1
      )}-${toTwoDigits(yesterdayDate.getDate())}`;
      const { currentYear, currentMonthNumber, lastMonthDate, targetMonths } = buildTargetMonths(today);
      const currentMonthKey = `${currentYear}-${toTwoDigits(currentMonthNumber)}`;
      const lastMonthKey = `${lastMonthDate.getFullYear()}-${toTwoDigits(lastMonthDate.getMonth() + 1)}`;

      const monthlyAggregate = {};
      const dailyAggregate = [];
      const yearlyData = {};
      let todayPayments = 0;
      let yesterdayPayments = 0;

      for (const { yearId, monthId } of targetMonths) {
        const daysCollection = collection(
          db,
          "analytics",
          "premiumSubscribers",
          "years",
          yearId,
          "months",
          monthId,
          "days"
        );
        const daysSnapshot = await getDocs(query(daysCollection));

        for (const dayDoc of daysSnapshot.docs) {
          const dayId = toTwoDigits(dayDoc.id);
          const date = new Date(Number(yearId), Number(monthId) - 1, Number(dayId));
          if (Number.isNaN(date.getTime())) {
            continue;
          }

          const monthKey = `${yearId}-${monthId}`;
          const yearKey = `${yearId}`;
          const dateKey = `${yearId}-${monthId}-${dayId}`;

          if (!monthlyAggregate[monthKey]) {
            monthlyAggregate[monthKey] = {
              net: 0,
              gross: 0,
              totalPayments: 0,
              newPayments: 0,
              recurringPayments: 0,
            };
          }

          if (!yearlyData[yearKey]) {
            yearlyData[yearKey] = { net: 0, gross: 0 };
          }

          const dailyStats = dayDoc.data();
          monthlyAggregate[monthKey].net += dailyStats.net || 0;
          monthlyAggregate[monthKey].gross += dailyStats.gross || 0;
          monthlyAggregate[monthKey].totalPayments += dailyStats.totalPayments || 0;
          monthlyAggregate[monthKey].newPayments += dailyStats.totalNew || 0;
          monthlyAggregate[monthKey].recurringPayments += dailyStats.totalRecurring || 0;

          if (dateKey === todayKey) {
            todayPayments += dailyStats.totalPayments || 0;
          }
          if (dateKey === yesterdayKey) {
            yesterdayPayments += dailyStats.totalPayments || 0;
          }

          yearlyData[yearKey].net += dailyStats.net || 0;
          yearlyData[yearKey].gross += dailyStats.gross || 0;

          const diffTime = Math.abs(today - date);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays <= 30) {
            dailyAggregate.push({
              date: dateKey,
              newPayments: dailyStats.totalNew || 0,
              recurringPayments: dailyStats.totalRecurring || 0,
            });
          }
        }
      }

      const currentMonthNet = monthlyAggregate[currentMonthKey]?.net || 0;
      const currentMonthProjected = (currentMonthNet / new Date().getDate()) * 30;

      setCurrentMonthData({
        net: formatNumber(currentMonthNet),
        projected: formatNumber(currentMonthProjected),
      });
      setRecentPaymentCounts({
        today: todayPayments,
        yesterday: yesterdayPayments,
      });

      const months = Object.keys(monthlyAggregate).sort();
      const graphNet = months.map((month) => monthlyAggregate[month].net);
      const graphGross = months.map((month) => monthlyAggregate[month].gross);
      const graphTotalPaymentsNew = months.map((month) => monthlyAggregate[month].newPayments);
      const graphTotalPaymentsRecurring = months.map(
        (month) => monthlyAggregate[month].recurringPayments
      );

      setMonthlyData(months);
      setGraphData({
        net: graphNet,
        gross: graphGross,
        totalPayments: {
          new: graphTotalPaymentsNew,
          recurring: graphTotalPaymentsRecurring,
        },
      });

      const sortedDaily = [...dailyAggregate].sort((a, b) => a.date.localeCompare(b.date));
      setDailyData(sortedDaily.map((entry) => entry.date));
      setDailyGraphData({
        totalPayments: {
          new: sortedDaily.map((entry) => entry.newPayments),
          recurring: sortedDaily.map((entry) => entry.recurringPayments),
        },
      });

      const lastMonthTotals = monthlyAggregate[lastMonthKey] || {
        net: 0,
        gross: 0,
        totalPayments: 0,
        newPayments: 0,
        recurringPayments: 0,
      };
      const ytdTotals = Object.entries(monthlyAggregate)
        .filter(([monthKey]) => monthKey.startsWith(`${currentYear}-`))
        .reduce(
          (acc, [, value]) => ({
            net: acc.net + (value.net || 0),
            gross: acc.gross + (value.gross || 0),
            totalPayments: acc.totalPayments + (value.totalPayments || 0),
            newPayments: acc.newPayments + (value.newPayments || 0),
            recurringPayments: acc.recurringPayments + (value.recurringPayments || 0),
          }),
          { net: 0, gross: 0, totalPayments: 0, newPayments: 0, recurringPayments: 0 }
        );

      setTableData(
        [
          {
            month: `This Month (${currentMonthKey})`,
            ...monthlyAggregate[currentMonthKey],
          },
          {
            month: `Last Month (${lastMonthKey})`,
            ...lastMonthTotals,
          },
          {
            month: `YTD (${currentYear})`,
            ...ytdTotals,
          },
        ].map((period) => ({
          month: period.month,
          profit: formatNumber(period.net || 0),
          gross: formatNumber(period.gross || 0),
          totalPayments: formatNumber(period.totalPayments || 0),
          newPayments: formatNumber(period.newPayments || 0),
          recurringPayments: formatNumber(period.recurringPayments || 0),
        }))
      );

      setYearlyTotals(yearlyData);
    };
    fetchPremiumAnalytics().catch((error) => {
      console.error("Error loading dashboard analytics:", error);
    });
  }, []);

  const orderedYears = useMemo(
    () => Object.entries(yearlyTotals).sort(([yearA], [yearB]) => yearB.localeCompare(yearA)),
    [yearlyTotals]
  );

  const monthProjectionData = useMemo(() => {
    const today = new Date();
    const currentMonthKey = `${today.getFullYear()}-${toTwoDigits(today.getMonth() + 1)}`;
    const lastCompletedMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastCompletedMonthKey = `${lastCompletedMonthDate.getFullYear()}-${toTwoDigits(
      lastCompletedMonthDate.getMonth() + 1
    )}`;

    const currentMonthIndex = monthlyData.indexOf(currentMonthKey);
    if (currentMonthIndex < 0) {
      return null;
    }

    const exactLastCompletedMonthIndex = monthlyData.indexOf(lastCompletedMonthKey);
    const fallbackLastCompletedMonthIndex = currentMonthIndex - 1;
    const lastCompletedMonthIndex =
      exactLastCompletedMonthIndex >= 0
        ? exactLastCompletedMonthIndex
        : fallbackLastCompletedMonthIndex;

    if (lastCompletedMonthIndex < 0) {
      return null;
    }

    const dayOfMonth = today.getDate();
    if (dayOfMonth <= 0) {
      return null;
    }

    const projectedNet = ((graphData.net[currentMonthIndex] || 0) / dayOfMonth) * 30;
    const projectedGross = ((graphData.gross[currentMonthIndex] || 0) / dayOfMonth) * 30;

    return {
      net: buildProjectionSeries({
        sourceSeries: graphData.net,
        fromIndex: lastCompletedMonthIndex,
        toIndex: currentMonthIndex,
        projectedValue: projectedNet,
      }),
      gross: buildProjectionSeries({
        sourceSeries: graphData.gross,
        fromIndex: lastCompletedMonthIndex,
        toIndex: currentMonthIndex,
        projectedValue: projectedGross,
      }),
    };
  }, [graphData.gross, graphData.net, monthlyData]);

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__hero">
        <h1 className="dashboard-page__title">Analytics Dashboard</h1>
        <p className="dashboard-page__subtitle">
          Current Month Net: <strong>{currentMonthData.net}</strong>
          <span className="divider">|</span>
          Projected: <strong>{currentMonthData.projected}</strong>
          <span className="divider">|</span>
          Today Payments: <strong>{formatNumber(recentPaymentCounts.today)}</strong>
          <span className="divider">|</span>
          Yesterday Payments: <strong>{formatNumber(recentPaymentCounts.yesterday)}</strong>
        </p>
      </div>

      <div className="dashboard-page__grid">
        <section className="dashboard-card">
          <h2>Net Over Time</h2>
          <div className="dashboard-chart">
            <Line
              data={{
                labels: monthlyData,
                datasets: [
                  {
                    label: "Net",
                    data: graphData.net,
                    borderColor: "#e07136",
                    borderWidth: 2,
                    fill: false,
                  },
                  ...(monthProjectionData
                    ? [
                        {
                          label: "Projected (This Month)",
                          data: monthProjectionData.net,
                          borderColor: chartProjectionColor,
                          pointBackgroundColor: chartProjectionColor,
                          pointBorderColor: chartProjectionColor,
                          borderWidth: 2,
                          borderDash: [5, 5],
                          pointRadius: 3,
                          fill: false,
                          spanGaps: false,
                        },
                      ]
                    : []),
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "top", labels: { color: chartTextColor } } },
                scales: {
                  x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                  y: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                },
              }}
            />
          </div>
        </section>

        <section className="dashboard-card">
          <h2>Gross Over Time</h2>
          <div className="dashboard-chart">
            <Line
              data={{
                labels: monthlyData,
                datasets: [
                  {
                    label: "Gross",
                    data: graphData.gross,
                    borderColor: "#596550",
                    borderWidth: 2,
                    fill: false,
                  },
                  ...(monthProjectionData
                    ? [
                        {
                          label: "Projected (This Month)",
                          data: monthProjectionData.gross,
                          borderColor: chartProjectionColor,
                          pointBackgroundColor: chartProjectionColor,
                          pointBorderColor: chartProjectionColor,
                          borderWidth: 2,
                          borderDash: [5, 5],
                          pointRadius: 3,
                          fill: false,
                          spanGaps: false,
                        },
                      ]
                    : []),
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "top", labels: { color: chartTextColor } } },
                scales: {
                  x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                  y: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
                },
              }}
            />
          </div>
        </section>

        <section className="dashboard-card dashboard-card--wide">
          <h2>Total Payments Over 12 Months</h2>
          <div className="dashboard-chart">
            <Bar
              data={{
                labels: monthlyData,
                datasets: [
                  {
                    label: "New Payments",
                    data: graphData.totalPayments.new,
                    backgroundColor: "rgba(224, 113, 54, 0.72)",
                  },
                  {
                    label: "Recurring Payments",
                    data: graphData.totalPayments.recurring,
                    backgroundColor: "rgba(89, 101, 80, 0.72)",
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: "top", labels: { color: chartTextColor } },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.dataset.label}: ${formatNumber(context.raw)}`,
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    ticks: { color: chartTextColor },
                    grid: { color: chartGridColor },
                  },
                  y: {
                    stacked: true,
                    ticks: { color: chartTextColor },
                    grid: { color: chartGridColor },
                  },
                },
              }}
            />
          </div>
        </section>

        <section className="dashboard-card dashboard-card--wide">
          <h2>Total Payments (Last 30 Days)</h2>
          <div className="dashboard-chart">
            <Bar
              data={{
                labels: dailyData,
                datasets: [
                  {
                    label: "New Payments",
                    data: dailyGraphData.totalPayments.new,
                    backgroundColor: "rgba(224, 113, 54, 0.72)",
                  },
                  {
                    label: "Recurring Payments",
                    data: dailyGraphData.totalPayments.recurring,
                    backgroundColor: "rgba(89, 101, 80, 0.72)",
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: "top", labels: { color: chartTextColor } },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.dataset.label}: ${formatNumber(context.raw)}`,
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    ticks: { color: chartTextColor },
                    grid: { color: chartGridColor },
                  },
                  y: {
                    stacked: true,
                    ticks: { color: chartTextColor },
                    grid: { color: chartGridColor },
                  },
                },
              }}
            />
          </div>
        </section>

      </div>

      <section className="dashboard-table-card">
        <h2>Yearly Totals</h2>
        <div className="dashboard-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Year</th>
                <th>Net</th>
                <th>Gross</th>
              </tr>
            </thead>
            <tbody>
              {orderedYears.map(([year, totals]) => (
                <tr key={year}>
                  <td>{year}</td>
                  <td>{formatNumber(totals.net)}</td>
                  <td>{formatNumber(totals.gross)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="dashboard-table-card">
        <h2>Monthly Summary</h2>
        <div className="dashboard-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Profit</th>
                <th>Gross</th>
                <th>Total Payments</th>
                <th>New Payments</th>
                <th>Recurring Payments</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={`${row.month}-${index}`}>
                  <td>{row.month}</td>
                  <td>{row.profit}</td>
                  <td>{row.gross}</td>
                  <td>{row.totalPayments}</td>
                  <td>{row.newPayments}</td>
                  <td>{row.recurringPayments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
};

export default Dashboard;
